/**
 * @fileoverview ConfirmationModal Component
 * 
 * A custom confirmation dialog that replaces browser's window.confirm()
 * with a styled modal matching the application's blue gradient design system.
 * 
 * Features:
 * - Blue gradient background with white border
 * - Icon-based visual feedback for different variants
 * - Loading state support
 * - Keyboard accessibility (ESC to cancel)
 * - Mobile responsive
 * - Prevents body scroll when open
 * 
 * @exports ConfirmationModal
 */

import { useEffect } from "react";
import "../styles/components/ConfirmationModal.css";

/**
 * ConfirmationModal Component
 * 
 * Displays a confirmation dialog with customizable title, message, and action buttons.
 * Used for critical actions like deletions that require user confirmation.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onConfirm - Callback invoked when user confirms action
 * @param {Function} props.onCancel - Callback invoked when user cancels or closes modal
 * @param {string} props.title - Modal title text
 * @param {string} props.message - Main confirmation message
 * @param {string} [props.confirmText="Confirm"] - Text for confirm button
 * @param {string} [props.cancelText="Cancel"] - Text for cancel button
 * @param {boolean} [props.loading=false] - Shows loading state on confirm button
 * @param {string} [props.variant="danger"] - Visual variant: "danger", "warning", "info"
 * @param {React.ReactNode} [props.details] - Optional additional details to display
 * @param {boolean} [props.isMobile=false] - Mobile view flag
 * 
 * @returns {JSX.Element|null} The confirmation modal or null if not open
 * 
 * @example
 * <ConfirmationModal
 *   isOpen={showModal}
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowModal(false)}
 *   title="Delete Resident"
 *   message="Are you sure you want to remove this resident?"
 *   confirmText="Delete"
 *   variant="danger"
 *   loading={isDeleting}
 * />
 */
export default function ConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  variant = "danger", // danger, warning, info
  details,
  isMobile = false,
}) {
  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape" && !loading) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, loading, onCancel]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Get icon based on variant
  const getIcon = () => {
    switch (variant) {
      case "danger":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        );
      case "warning":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4m0 4h.01"/>
          </svg>
        );
      case "info":
        return (
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4m0-4h.01"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`confirmation-modal-overlay ${isMobile ? "mobile" : ""}`}>
      <div 
        className="confirmation-modal-backdrop"
        onClick={!loading ? onCancel : undefined}
      />
      
      <div className={`confirmation-modal ${variant} ${isMobile ? "mobile" : ""}`}>
        {/* Icon */}
        <div className={`confirmation-modal__icon confirmation-modal__icon--${variant}`}>
          {getIcon()}
        </div>

        {/* Title */}
        <h2 className="confirmation-modal__title">
          {title}
        </h2>

        {/* Subtitle */}
        <p className="confirmation-modal__subtitle">
          This action requires confirmation
        </p>

        {/* Message */}
        <p className="confirmation-modal__message">
          {message}
        </p>
        
        {/* Optional Details */}
        {details && (
          <div className="confirmation-modal__details">
            {details}
          </div>
        )}

        {/* Action Buttons */}
        <div className="confirmation-modal__actions">
          <button
            onClick={onCancel}
            disabled={loading}
            className="confirmation-modal__button confirmation-modal__button--cancel"
            type="button"
          >
            {cancelText}
          </button>
          
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`confirmation-modal__button confirmation-modal__button--confirm confirmation-modal__button--${variant}`}
            type="button"
          >
            {loading ? (
              <>
                <svg className="confirmation-modal__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                </svg>
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
